import React, { useState, useCallback, useEffect } from 'react';
import API from 'services/api';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getRegionId } from 'reducers/users';
import debounce from 'lodash/debounce';
import Select from '../Select';
import { InfoPropTypes } from 'components/Tooltip';
import { ADDRESS_MARKER } from '../../../../utils/constants/Map';

const Address = ({
  regionId,
  name,
  className,
  onFocus,
  required,
  info,
  label,
  placeholder,
  error,
  value,
  onChange,
  freeSolo,
  autoSelect,
}) => {
  const [result, setResult] = useState([]);
  const [typedValue, setTypedValue] = useState('');

  const plotMarker = value => {
    if (value) {
      const lat = value.latitude;
      const lng = value.longitude;
      return [
        {
          icon: ADDRESS_MARKER.icon,
          lat,
          lng,
        },
      ];
    }
    return null;
  };

  const handleChange = (e, inputValue) => {
    if (!e) {
      return null;
    }
    if (inputValue && inputValue.length <= 2) {
      setTypedValue(inputValue);
    }

    if (inputValue && inputValue.length > 2) {
      return fetchAddressData(inputValue);
    }
    return null;
  };

  const updateChange = data => {
    const newData = data || {};
    if (typeof newData === 'string') {
      // incase values are not recognised by google, they are sent as strings
      const newAddress = { id: newData, name: newData, locations: {} }; // create an object which google creates
      newAddress.markers = plotMarker(data ? data.locations : []);
      onChange(newAddress);
    } else {
      newData.markers = plotMarker(data ? data.locations : []);
      onChange(newData);
    }
  };

  const getOptions = result => {
    return result
      ? result.map(key => ({
          id: key.address,
          name: key.address,
          locations: key.point,
        }))
      : value;
  };

  const fetchAddressData = useCallback(
    debounce(async search => {
      try {
        const { data } = await API.fetchAddressList({
          regionId,
          search,
        });
        if (data.data.locations) {
          setResult(data.data.locations);
        }
      } catch (err) {
        setResult([]);
      }
    }, 350),
    [],
  );

  useEffect(() => {
    if (value) {
      fetchAddressData(value);
    }
  }, [fetchAddressData, value]);

  return (
    <Select
      freeSolo={freeSolo}
      autoSelect={autoSelect}
      useSameValue
      blurOnSelect
      placeholder={placeholder}
      error={error}
      value={value ? value : typedValue}
      name={name}
      label={label}
      onFocus={onFocus}
      info={info}
      required={required}
      className={className}
      onChange={updateChange}
      useSelectedValue
      onInputChange={handleChange}
      options={getOptions(result)}
    />
  );
};

Address.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  required: PropTypes.bool,
  info: InfoPropTypes,
  autoComplete: PropTypes.string,
  label: PropTypes.string,
  regionId: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      locations: PropTypes.object,
      longitude: PropTypes.string,
      latitude: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func,
  freeSolo: PropTypes.bool,
  autoSelect: PropTypes.bool,
};

const mapStateToProps = state => ({
  regionId: getRegionId(state),
});

export default connect(mapStateToProps)(Address);
